import type { GitPushError } from '../worker';

export enum GitEventType {
  BranchCreated = 'branch_created',
  BranchMerged = 'branch_merged',
  BranchChanged = 'branch_changed',
  BranchPulled = 'branch_pulled',
  ForceUpdateUI = 'force_update_ui',
  ChangesCommitted = 'changes_committed',
  ChangesStashed = 'changes_stashed',
  ChangesRestored = 'changes_restored',
  ChangesDiscarded = 'changes_discarded',
  ChangesReverted = 'changes_reverted',

  PushFailed = 'push_failed',
  PushSucceeded = 'push_succeeded',
}

export interface GitEvent<Params = unknown> {
  readonly type: GitEventType;
  readonly params?: Params;
}

type GitEvents =
  | GitEventBranchCreated
  | GitEventBranchMerged
  | GitEventBranchChanged
  | GitEventBranchPulled
  | GitEventForceUpdateUI
  | GitEventChangesCommitted
  | GitEventChangesStashed
  | GitEventChangesRestored
  | GitEventChangesDiscarded
  | GitEventChangesReverted
  | GitEventPushFailed
  | GitEventPushSucceeded;

export type GitEventTypeToGitEvent = {
  [Type in GitEvents['type']]: GitEvents extends infer Event
    ? Event extends GitEvent
      ? Event['type'] extends Type
        ? Event
        : never
      : never
    : never;
};

export interface GitEventBranchCreated extends GitEvent {
  type: GitEventType.BranchCreated;
  params: { from: string; name: string };
}

export interface GitEventBranchMerged extends GitEvent {
  type: GitEventType.BranchMerged;
  params: { source: string; target: string };
}

export interface GitEventBranchChanged extends GitEvent {
  type: GitEventType.BranchChanged;
  params: { to: string };
}

export interface GitEventBranchPulled extends GitEvent {
  type: GitEventType.BranchPulled;
  params: { branch: string };
}

export interface GitEventChangesStashed extends GitEvent {
  type: GitEventType.ChangesStashed;
  params: { branch: string };
}

export interface GitEventChangesRestored extends GitEvent {
  type: GitEventType.ChangesRestored;
  params: { branch: string };
}

export interface GitEventChangesCommitted extends GitEvent {
  type: GitEventType.ChangesCommitted;
  params: { paths?: string[] };
}

export interface GitEventChangesDiscarded extends GitEvent {
  type: GitEventType.ChangesDiscarded;
  params: { branch: string };
}

export interface GitEventChangesReverted extends GitEvent {
  type: GitEventType.ChangesReverted;
  params: { branch: string };
}

export interface GitEventForceUpdateUI extends GitEvent {
  type: GitEventType.ForceUpdateUI;
  params: {
    branches?: boolean;
    files?: boolean;
    aheadBehind?: boolean;
    commitSha?: boolean;
    activeBranch?: boolean;
  };
}

export interface GitEventPushFailed extends GitEvent {
  type: GitEventType.PushFailed;
  params: { err: GitPushError };
}

export interface GitEventPushSucceeded extends GitEvent {
  type: GitEventType.PushSucceeded;
}
